import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { C, exportCSV, fmtFull, fmtUSD, toNum } from "../reporting/reportingUtils";

export const BILL_TENANT_ID = "vuffR0CbVJmdSwxprNIb";

const VIEW_STORAGE_KEY = "bill-insurance-coverage-table-view-v2";
const TYPE_META = {
  maintain_requested_limit: { label: "Supported", color: C.green },
  spend_validation: { label: "Spend proof", color: C.amber },
  parent_support: { label: "Parent support", color: C.violet },
  sponsor_support: { label: "Sponsor support", color: C.violet },
  legal_compliance: { label: "Legal review", color: C.red },
  entity_verification: { label: "Verify entity", color: C.red },
  adverse_evidence_remediation: { label: "Adverse evidence", color: C.red },
  liquidity_funding: { label: "Liquidity", color: C.amber },
  financial_evidence: { label: "Financials", color: C.amber },
};

const COLUMN_DEFS = [
  { id: "company", label: "Company", width: 260 },
  { id: "status", label: "Coverage status", width: 170 },
  { id: "requested", label: "Requested", width: 145 },
  { id: "supported", label: "Supported now", width: 145 },
  { id: "gap", label: "Gap", width: 140 },
  { id: "coverage", label: "Coverage", width: 125 },
  { id: "arc", label: "ARC", width: 85 },
  { id: "route", label: "Primary route", width: 190 },
];
const DEFAULT_VISIBLE_COLUMNS = COLUMN_DEFS.map((column) => column.id);
const DEFAULT_COLUMN_FILTERS = {
  status: "all",
  route: "all",
  country: "",
  sector: "",
  minRequested: "",
  maxRequested: "",
  minSupported: "",
  maxGap: "",
  minCoverage: "",
  maxCoverage: "",
  minArc: "",
  maxArc: "",
};
const DENSITY_PADDING = { compact: 0.55, standard: 1, comfortable: 1.45 };

export function buildBillCoverageRows(rows = []) {
  return rows
    .map((row) => {
      const requested = Math.max(0, toNum(row.requestedLimit));
      const supported = Math.max(0, toNum(row.recLimit));
      if (!(requested > 0)) return null;
      const gap = Math.max(0, requested - supported);
      const ratio = Math.min(1, supported / requested);
      const coverageStatus = supported >= requested ? "full" : supported > 0 ? "partial" : "none";
      return {
        ...row,
        requested,
        supported,
        gap,
        ratio,
        coverageStatus,
        reasonType: row.coveragePathType || (coverageStatus === "full" ? "maintain_requested_limit" : "financial_evidence"),
      };
    })
    .filter(Boolean);
}

export function summarizeBillCoverage(rows = []) {
  return rows.reduce((result, row) => {
    result.requested += row.requested;
    result.supported += Math.min(row.supported, row.requested);
    result.gap += row.gap;
    result.full += row.coverageStatus === "full" ? 1 : 0;
    result.partial += row.coverageStatus === "partial" ? 1 : 0;
    result.none += row.coverageStatus === "none" ? 1 : 0;
    return result;
  }, { requested: 0, supported: 0, gap: 0, full: 0, partial: 0, none: 0 });
}

export function matchesBillCoverageFilter(row, filter) {
  if (filter === "full") return row.coverageStatus === "full";
  if (filter === "below") return row.coverageStatus !== "full";
  if (filter === "none") return row.coverageStatus === "none";
  if (filter === "parent") return ["parent_support", "sponsor_support"].includes(row.reasonType);
  if (filter === "spend") return row.reasonType === "spend_validation";
  if (filter === "legal") return ["legal_compliance", "adverse_evidence_remediation"].includes(row.reasonType);
  if (filter === "identity") return row.reasonType === "entity_verification";
  return true;
}

const numericFilterMatches = (value, minimum, maximum) => {
  if (minimum !== "" && value < Number(minimum)) return false;
  if (maximum !== "" && value > Number(maximum)) return false;
  return true;
};

export function matchesBillColumnFilters(row, filters = DEFAULT_COLUMN_FILTERS) {
  if (filters.status !== "all" && row.coverageStatus !== filters.status) return false;
  if (filters.route !== "all" && row.reasonType !== filters.route) return false;
  if (filters.country && !String(row.country || "").toLowerCase().includes(filters.country.toLowerCase())) return false;
  if (filters.sector && !String(row.sector || "").toLowerCase().includes(filters.sector.toLowerCase())) return false;
  if (!numericFilterMatches(row.requested, filters.minRequested, filters.maxRequested)) return false;
  if (!numericFilterMatches(row.supported, filters.minSupported, "")) return false;
  if (!numericFilterMatches(row.gap, "", filters.maxGap)) return false;
  if (!numericFilterMatches(Math.round(row.ratio * 100), filters.minCoverage, filters.maxCoverage)) return false;
  if (!numericFilterMatches(toNum(row.score), filters.minArc, filters.maxArc)) return false;
  return true;
}

export function sortBillCoverageRows(rows, sort) {
  const direction = sort.direction === "asc" ? 1 : -1;
  const stringValue = (row, field) => {
    if (field === "company") return row.name || "";
    if (field === "status") return row.coverageStatus || "";
    if (field === "route") return TYPE_META[row.reasonType]?.label || row.reasonType || "";
    return "";
  };
  const numericValue = (row, field) => {
    if (field === "coverage") return row.ratio;
    if (field === "arc") return toNum(row.score);
    return toNum(row[field]);
  };
  return [...rows].sort((a, b) => {
    const isString = ["company", "status", "route"].includes(sort.field);
    const comparison = isString
      ? String(stringValue(a, sort.field)).localeCompare(String(stringValue(b, sort.field)))
      : numericValue(a, sort.field) - numericValue(b, sort.field);
    return comparison * direction || String(a.name || "").localeCompare(String(b.name || ""));
  });
}

const FILTERS = [
  { key: "all", label: "All requested" },
  { key: "full", label: "Fully supported" },
  { key: "below", label: "Below requested" },
  { key: "none", label: "No supported limit" },
  { key: "parent", label: "Parent / sponsor route" },
  { key: "spend", label: "Needs spend proof" },
  { key: "legal", label: "Legal / adverse review" },
  { key: "identity", label: "Identity check" },
];

const statusMeta = (status) => status === "full"
  ? { label: "Fully supported", color: C.green, bg: "#E7F4E7" }
  : status === "partial"
    ? { label: "Below requested", color: "#A66500", bg: "#FFF3DE" }
    : { label: "No supported limit", color: C.red, bg: "#FDECEC" };

const Kpi = ({ label, value, sub, color = C.ink }) => (
  <Box sx={{ p: 2, bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: 2.5 }}>
    <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".055em" }}>{label}</Typography>
    <Typography sx={{ mt: .5, fontSize: 25, lineHeight: 1.1, color, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    <Typography sx={{ mt: .5, fontSize: 12, color: C.muted }}>{sub}</Typography>
  </Box>
);

const NumberFilter = ({ label, value, onChange, suffix }) => (
  <TextField
    size="small"
    type="number"
    label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    inputProps={{ min: 0 }}
    InputProps={suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : undefined}
  />
);

const loadSavedView = () => {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(window.localStorage.getItem(VIEW_STORAGE_KEY));
  } catch {
    return null;
  }
};

export default function BillInsuranceCoverageDashboard({ tenantId, rows, isAdmin, onRequestCover }) {
  const savedAtLoad = useMemo(loadSavedView, []);
  const coverageRows = useMemo(() => buildBillCoverageRows(rows), [rows]);
  const totals = useMemo(() => summarizeBillCoverage(coverageRows), [coverageRows]);
  const [filter, setFilter] = useState(savedAtLoad?.filter || "all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(savedAtLoad?.sort || { field: "gap", direction: "desc" });
  const [columnFilters, setColumnFilters] = useState({ ...DEFAULT_COLUMN_FILTERS, ...savedAtLoad?.columnFilters });
  const [visibleColumns, setVisibleColumns] = useState(savedAtLoad?.visibleColumns || DEFAULT_VISIBLE_COLUMNS);
  const [density, setDensity] = useState(savedAtLoad?.density || "standard");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((item) => [
    item.key,
    coverageRows.filter((row) => matchesBillCoverageFilter(row, item.key)).length,
  ])), [coverageRows]);

  const activeColumnFilterCount = useMemo(() => Object.entries(columnFilters)
    .filter(([key, value]) => value !== "" && !(key === "status" && value === "all") && !(key === "route" && value === "all"))
    .length, [columnFilters]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const result = coverageRows.filter((row) => {
      if (!matchesBillCoverageFilter(row, filter) || !matchesBillColumnFilters(row, columnFilters)) return false;
      if (!needle) return true;
      return [row.name, row.country, row.sector, row.parentName, TYPE_META[row.reasonType]?.label]
        .some((value) => String(value || "").toLowerCase().includes(needle));
    });
    return sortBillCoverageRows(result, sort);
  }, [coverageRows, filter, query, columnFilters, sort]);

  const visible = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const visibleIds = visible.map((row) => row.companyId || row.name);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id)) && !allVisibleSelected;
  const selectedRows = coverageRows.filter((row) => selected.has(row.companyId || row.name));
  const coveragePct = totals.requested ? Math.round((totals.supported / totals.requested) * 100) : 0;
  const visibleColumnSet = new Set(visibleColumns);
  const tableColSpan = visibleColumns.length + 3;
  const rowPadding = DENSITY_PADDING[density] || DENSITY_PADDING.standard;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filtered.length / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filtered.length, page, pageSize]);

  const changeFilter = (next) => { setFilter(next); setPage(0); setSelected(new Set()); };
  const updateColumnFilter = (key, value) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
    setPage(0);
  };
  const resetConfiguration = () => {
    setFilter("all");
    setQuery("");
    setSort({ field: "gap", direction: "desc" });
    setColumnFilters({ ...DEFAULT_COLUMN_FILTERS });
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setDensity("standard");
    setPage(0);
    setSelected(new Set());
  };
  const saveCurrentView = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify({ filter, sort, columnFilters, visibleColumns, density }));
    }
    setSettingsAnchor(null);
  };
  const openCompany = (companyId) => {
    const path = isAdmin
      ? `/tenant-dashboard/${tenantId}/company/${companyId}`
      : `/company/${companyId}`;
    window.open(path, "_blank", "noopener,noreferrer");
  };
  const toggleSort = (field) => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };
  const toggleColumn = (columnId) => {
    setVisibleColumns((current) => {
      if (current.length === 1 && current.includes(columnId)) return current;
      return current.includes(columnId)
        ? current.filter((id) => id !== columnId)
        : COLUMN_DEFS.filter((column) => [...current, columnId].includes(column.id)).map((column) => column.id);
    });
  };
  const toggleRow = (rowId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };
  const toggleVisibleRows = () => {
    setSelected((current) => {
      const next = new Set(current);
      visibleIds.forEach((id) => allVisibleSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };
  const exportRows = (rowsToExport = filtered, suffix = "filtered") => exportCSV(rowsToExport.map((row) => ({
    company: row.name,
    status: statusMeta(row.coverageStatus).label,
    requested: row.requested,
    supported: row.supported,
    gap: row.gap,
    coveragePct: `${Math.round(row.ratio * 100)}%`,
    arc: row.score == null ? "" : Number(row.score).toFixed(1),
    country: row.country || "",
    sector: row.sector || "",
    reason: TYPE_META[row.reasonType]?.label || row.reasonType,
    parent: row.parentName || "",
    explainability: row.explainability || row.coveragePathSummary || "",
    nextStep: row.coveragePrimaryAction || row.coveragePathSummary || "Manual coverage review",
  })), [
    { key: "company", label: "Company" }, { key: "status", label: "Coverage Status" },
    { key: "requested", label: "Requested Coverage" }, { key: "supported", label: "Supported Now" },
    { key: "gap", label: "Coverage Gap" }, { key: "coveragePct", label: "Coverage %" },
    { key: "arc", label: "ARC Score" }, { key: "country", label: "Country" },
    { key: "sector", label: "Sector" }, { key: "reason", label: "Primary Route" },
    { key: "parent", label: "Parent / Sponsor" }, { key: "explainability", label: "Explainability" },
    { key: "nextStep", label: "Next Step" },
  ], `Bill-insurance-coverage-${suffix}`);

  const renderHeader = (column) => (
    visibleColumnSet.has(column.id) && (
      <TableCell
        key={column.id}
        sortDirection={sort.field === column.id ? sort.direction : false}
        sx={{
          minWidth: column.width,
          py: 1.05,
          color: C.muted,
          fontSize: 10.5,
          fontWeight: 850,
          textTransform: "uppercase",
          letterSpacing: ".05em",
          bgcolor: "#F8FAFD",
          ...(column.id === "company" ? { position: "sticky", left: 82, zIndex: 4 } : {}),
        }}
      >
        <TableSortLabel
          active={sort.field === column.id}
          direction={sort.field === column.id ? sort.direction : "asc"}
          onClick={() => toggleSort(column.id)}
          sx={{ "& .MuiTableSortLabel-icon": { opacity: sort.field === column.id ? 1 : .32 } }}
        >
          {column.label}
        </TableSortLabel>
      </TableCell>
    )
  );

  return (
    <Box sx={{ mb: 2.5 }}>
      <Alert severity="info" icon={<ShieldOutlinedIcon />} sx={{ mb: 1.5, border: "1px solid #CFE0FF", bgcolor: "#F5F8FF" }}>
        <strong>Bill credit-card insurance view.</strong> Requested coverage is Bill’s target for approximately 30 days of card exposure. “Supported now” is the current underwriting recommendation; a gap is a conditional path, not automatically a decline.
      </Alert>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(5, 1fr)" }, gap: 1.25, mb: 1.5 }}>
        <Kpi label="Requested coverage" value={fmtUSD(totals.requested)} sub={`${coverageRows.length.toLocaleString()} companies`} />
        <Kpi label="Supported now" value={fmtUSD(totals.supported)} sub={`${coveragePct}% of requested`} color={C.green} />
        <Kpi label="Coverage gap" value={fmtUSD(totals.gap)} sub={`${(totals.partial + totals.none).toLocaleString()} below request`} color={C.amber} />
        <Kpi label="Fully supported" value={totals.full.toLocaleString()} sub={`${coverageRows.length ? Math.round((totals.full / coverageRows.length) * 100) : 0}% of companies`} color={C.green} />
        <Kpi label="No supported limit" value={totals.none.toLocaleString()} sub="Requires review or remediation" color={totals.none ? C.red : C.ink} />
      </Box>

      <Box sx={{ p: 2, mb: 1.5, bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: 2.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 800 }}>Portfolio coverage against Bill’s requested limits</Typography>
          <Typography sx={{ fontSize: 13, color: C.muted, fontWeight: 700 }}>{coveragePct}% currently supported</Typography>
        </Box>
        <LinearProgress variant="determinate" value={coveragePct} sx={{ height: 9, borderRadius: 8, bgcolor: "#EEF1F7", "& .MuiLinearProgress-bar": { borderRadius: 8, bgcolor: C.green } }} />
      </Box>

      <Box sx={{ bgcolor: "#fff", border: `1px solid ${C.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${C.line}` }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 850, color: C.ink }}>Coverage decisions</Typography>
              <Typography sx={{ fontSize: 12.5, color: C.muted }}>
                {filtered.length.toLocaleString()} of {coverageRows.length.toLocaleString()} companies · click any column heading to sort
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: .75, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                value={query}
                onChange={(event) => { setQuery(event.target.value); setPage(0); }}
                placeholder="Search company, country, sector…"
                sx={{ width: { xs: "100%", sm: 280 } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              />
              <Badge badgeContent={activeColumnFilterCount} color="primary">
                <Button variant={filterPanelOpen ? "contained" : "outlined"} startIcon={<FilterAltOutlinedIcon />} onClick={() => setFilterPanelOpen((open) => !open)} sx={{ textTransform: "none" }}>Filters</Button>
              </Badge>
              <Tooltip title="Choose columns">
                <IconButton aria-label="Choose columns" onClick={(event) => setColumnsAnchor(event.currentTarget)} sx={{ border: `1px solid ${C.line}`, borderRadius: 1.5 }}><ViewColumnOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title="Table settings">
                <IconButton aria-label="Table settings" onClick={(event) => setSettingsAnchor(event.currentTarget)} sx={{ border: `1px solid ${C.line}`, borderRadius: 1.5 }}><SettingsOutlinedIcon fontSize="small" /></IconButton>
              </Tooltip>
              <Button variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={() => exportRows()} sx={{ textTransform: "none" }}>Export view</Button>
            </Box>
          </Box>

          <Collapse in={filterPanelOpen}>
            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#F8FAFD", border: `1px solid ${C.line}`, borderRadius: 2 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.25 }}>
                <TextField select size="small" label="Coverage status" value={columnFilters.status} onChange={(event) => updateColumnFilter("status", event.target.value)}>
                  <MenuItem value="all">All statuses</MenuItem><MenuItem value="full">Fully supported</MenuItem><MenuItem value="partial">Below requested</MenuItem><MenuItem value="none">No supported limit</MenuItem>
                </TextField>
                <TextField select size="small" label="Primary route" value={columnFilters.route} onChange={(event) => updateColumnFilter("route", event.target.value)}>
                  <MenuItem value="all">All routes</MenuItem>
                  {Object.entries(TYPE_META).map(([value, meta]) => <MenuItem key={value} value={value}>{meta.label}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Country contains" value={columnFilters.country} onChange={(event) => updateColumnFilter("country", event.target.value)} />
                <TextField size="small" label="Sector contains" value={columnFilters.sector} onChange={(event) => updateColumnFilter("sector", event.target.value)} />
                <NumberFilter label="Minimum requested" value={columnFilters.minRequested} onChange={(value) => updateColumnFilter("minRequested", value)} />
                <NumberFilter label="Maximum requested" value={columnFilters.maxRequested} onChange={(value) => updateColumnFilter("maxRequested", value)} />
                <NumberFilter label="Minimum supported" value={columnFilters.minSupported} onChange={(value) => updateColumnFilter("minSupported", value)} />
                <NumberFilter label="Maximum gap" value={columnFilters.maxGap} onChange={(value) => updateColumnFilter("maxGap", value)} />
                <NumberFilter label="Minimum coverage" value={columnFilters.minCoverage} onChange={(value) => updateColumnFilter("minCoverage", value)} suffix="%" />
                <NumberFilter label="Maximum coverage" value={columnFilters.maxCoverage} onChange={(value) => updateColumnFilter("maxCoverage", value)} suffix="%" />
                <NumberFilter label="Minimum ARC" value={columnFilters.minArc} onChange={(value) => updateColumnFilter("minArc", value)} />
                <NumberFilter label="Maximum ARC" value={columnFilters.maxArc} onChange={(value) => updateColumnFilter("maxArc", value)} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mt: 1.25, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12, color: C.muted }}>{activeColumnFilterCount ? `${activeColumnFilterCount} advanced filters active` : "Add one or more filters; results update immediately."}</Typography>
                <Button size="small" startIcon={<RestartAltIcon />} onClick={() => setColumnFilters({ ...DEFAULT_COLUMN_FILTERS })} sx={{ textTransform: "none" }}>Clear advanced filters</Button>
              </Box>
            </Box>
          </Collapse>

          <Box sx={{ display: "flex", gap: .75, mt: 1.5, flexWrap: "wrap" }}>
            {FILTERS.map((item) => <Chip key={item.key} clickable onClick={() => changeFilter(item.key)} label={`${item.label} · ${counts[item.key] || 0}`} variant={filter === item.key ? "filled" : "outlined"} sx={{ fontWeight: 700, bgcolor: filter === item.key ? "#E9F0FD" : "#fff", color: filter === item.key ? C.blue : C.slate, borderColor: filter === item.key ? "#BFD0F4" : C.line }} />)}
          </Box>
        </Box>

        {selected.size > 0 && (
          <Box sx={{ px: 2, py: 1, bgcolor: "#EEF4FF", borderBottom: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 13, color: C.ink, fontWeight: 800 }}>{selected.size.toLocaleString()} selected</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={() => exportRows(selectedRows, "selected")} sx={{ textTransform: "none" }}>Export selected</Button>
              <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: "none" }}>Clear selection</Button>
            </Box>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1120 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFD" }}>
                <TableCell padding="checkbox" sx={{ bgcolor: "#F8FAFD", position: "sticky", left: 0, zIndex: 5 }}>
                  <Checkbox size="small" checked={allVisibleSelected} indeterminate={someVisibleSelected} onChange={toggleVisibleRows} inputProps={{ "aria-label": "Select all rows on this page" }} />
                </TableCell>
                <TableCell width={42} sx={{ bgcolor: "#F8FAFD", position: "sticky", left: 48, zIndex: 5 }} />
                {COLUMN_DEFS.map(renderHeader)}
                <TableCell width={54} sx={{ bgcolor: "#F8FAFD" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => {
                const rowId = row.companyId || row.name;
                const status = statusMeta(row.coverageStatus);
                const reason = TYPE_META[row.reasonType] || { label: "Review", color: C.muted };
                const isOpen = expanded === rowId;
                const isSelected = selected.has(rowId);
                const stickyCell = { position: "sticky", left: 82, zIndex: 2, bgcolor: isSelected ? "#F4F7FF" : "#fff" };
                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover
                      selected={isSelected}
                      sx={{ cursor: "pointer", "& td": { borderBottom: isOpen ? 0 : `1px solid ${C.line}`, py: rowPadding }, "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "#F4F7FF" } }}
                      onClick={() => setExpanded(isOpen ? null : rowId)}
                    >
                      <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()} sx={{ position: "sticky", left: 0, zIndex: 3, bgcolor: isSelected ? "#F4F7FF" : "#fff" }}>
                        <Checkbox size="small" checked={isSelected} onChange={() => toggleRow(rowId)} inputProps={{ "aria-label": `Select ${row.name}` }} />
                      </TableCell>
                      <TableCell sx={{ position: "sticky", left: 48, zIndex: 3, bgcolor: isSelected ? "#F4F7FF" : "#fff" }}>
                        <IconButton size="small" aria-label={isOpen ? "Hide coverage details" : "Show coverage details"}>{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                      </TableCell>
                      {visibleColumnSet.has("company") && <TableCell sx={stickyCell}><Typography sx={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{row.name}</Typography><Typography sx={{ fontSize: 11.5, color: C.muted }}>{[row.country, row.sector].filter(Boolean).join(" · ")}</Typography></TableCell>}
                      {visibleColumnSet.has("status") && <TableCell><Chip size="small" label={status.label} sx={{ height: 23, fontWeight: 750, color: status.color, bgcolor: status.bg }} /></TableCell>}
                      {visibleColumnSet.has("requested") && <TableCell sx={{ fontWeight: 750, fontVariantNumeric: "tabular-nums" }}>{fmtFull(row.requested)}</TableCell>}
                      {visibleColumnSet.has("supported") && <TableCell sx={{ color: row.supported ? C.green : C.red, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{fmtFull(row.supported)}</TableCell>}
                      {visibleColumnSet.has("gap") && <TableCell sx={{ color: row.gap ? "#A66500" : C.muted, fontWeight: 750, fontVariantNumeric: "tabular-nums" }}>{fmtFull(row.gap)}</TableCell>}
                      {visibleColumnSet.has("coverage") && <TableCell><Box sx={{ minWidth: 90 }}><Typography sx={{ fontSize: 12, fontWeight: 800 }}>{Math.round(row.ratio * 100)}%</Typography><LinearProgress variant="determinate" value={row.ratio * 100} sx={{ height: 5, borderRadius: 4, bgcolor: "#EEF1F7", "& .MuiLinearProgress-bar": { bgcolor: row.coverageStatus === "full" ? C.green : row.coverageStatus === "partial" ? C.amber : C.red } }} /></Box></TableCell>}
                      {visibleColumnSet.has("arc") && <TableCell><Chip size="small" label={row.score == null ? "—" : Number(row.score).toFixed(1)} sx={{ height: 23, minWidth: 42, fontWeight: 850 }} /></TableCell>}
                      {visibleColumnSet.has("route") && <TableCell><Chip size="small" variant="outlined" label={reason.label} sx={{ height: 23, color: reason.color, borderColor: `${reason.color}66`, fontWeight: 750 }} />{row.parentName && <Typography sx={{ mt: .4, maxWidth: 170, fontSize: 11, color: C.muted }} noWrap>{row.parentName}</Typography>}</TableCell>}
                      <TableCell onClick={(event) => event.stopPropagation()}><Tooltip title="Open company assessment"><IconButton size="small" onClick={() => openCompany(row.companyId)}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={tableColSpan} sx={{ p: 0, borderBottom: `1px solid ${C.line}` }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2.25, bgcolor: "#FBFCFE", display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.35fr 1fr" }, gap: 2 }}>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 850, textTransform: "uppercase" }}>Decision explanation</Typography>
                              <Typography sx={{ mt: .6, fontSize: 13, color: C.slate, lineHeight: 1.55 }}>{row.explainability || row.coveragePathSummary || "The current recommendation is based on the available underwriting evidence."}</Typography>
                              <Typography sx={{ mt: 1.5, fontSize: 11, color: C.muted, fontWeight: 850, textTransform: "uppercase" }}>Next step</Typography>
                              <Typography sx={{ mt: .6, fontSize: 13, color: C.ink, lineHeight: 1.5, fontWeight: 650 }}>{row.coveragePrimaryAction || row.coveragePathSummary || "Review the requested coverage with Credit Risk."}</Typography>
                              {row.coverageReunderwriteTrigger && <Typography sx={{ mt: 1, fontSize: 12, color: C.muted }}><strong>Re-underwrite when:</strong> {row.coverageReunderwriteTrigger}</Typography>}
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 850, textTransform: "uppercase" }}>Evidence and structures</Typography>
                              {(row.coverageEvidenceRequired || []).length ? <Box component="ul" sx={{ mt: .7, mb: 1, pl: 2.2 }}>{row.coverageEvidenceRequired.map((item) => <Typography component="li" key={item} sx={{ mb: .45, fontSize: 12.5, color: C.slate }}>{item}</Typography>)}</Box> : <Typography sx={{ mt: .7, fontSize: 12.5, color: C.muted }}>No additional evidence list recorded.</Typography>}
                              {(row.coverageAlternatives || []).slice(0, 2).map((item) => <Typography key={item.type || item.recommendation} sx={{ mt: .6, fontSize: 12.25, color: C.slate }}><strong>{String(item.type || "Alternative").replace(/_/g, " ")}:</strong> {item.recommendation}</Typography>)}
                              <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                                <Button size="small" variant="contained" onClick={() => onRequestCover({ product: "single_risk", companyId: row.companyId, companyName: row.name, requestedLimit: row.requested, arcScore: row.score, sector: row.sector, country: row.country, source: "bill_coverage_dashboard" })} sx={{ textTransform: "none" }}>Request coverage</Button>
                                <Button size="small" variant="outlined" onClick={() => openCompany(row.companyId)} sx={{ textTransform: "none" }}>Open assessment</Button>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {!visible.length && <TableRow><TableCell colSpan={tableColSpan} sx={{ py: 7, textAlign: "center" }}><Typography sx={{ color: C.ink, fontWeight: 800 }}>No companies match this view</Typography><Typography sx={{ mt: .5, color: C.muted, fontSize: 12.5 }}>Clear filters or broaden the search to see more decisions.</Typography><Button size="small" startIcon={<RestartAltIcon />} onClick={resetConfiguration} sx={{ mt: 1, textTransform: "none" }}>Reset table</Button></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Typography sx={{ pl: 2, fontSize: 12, color: C.muted }}>
            Sorted by <strong>{COLUMN_DEFS.find((column) => column.id === sort.field)?.label}</strong> {sort.direction === "asc" ? "ascending" : "descending"}
          </Typography>
          <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, next) => setPage(next)} rowsPerPage={pageSize} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} showFirstButton showLastButton />
        </Box>
      </Box>

      <Menu anchorEl={columnsAnchor} open={Boolean(columnsAnchor)} onClose={() => setColumnsAnchor(null)} PaperProps={{ sx: { minWidth: 230, p: .5 } }}>
        <Typography sx={{ px: 1.5, py: 1, color: C.ink, fontSize: 12, fontWeight: 850 }}>Visible columns</Typography>
        <Divider />
        {COLUMN_DEFS.map((column) => (
          <MenuItem key={column.id} dense disableRipple onClick={() => toggleColumn(column.id)}>
            <FormControlLabel
              control={<Checkbox size="small" checked={visibleColumnSet.has(column.id)} disabled={visibleColumns.length === 1 && visibleColumnSet.has(column.id)} />}
              label={column.label}
              sx={{ m: 0, width: "100%" }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem dense onClick={() => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS)}>Show all columns</MenuItem>
      </Menu>

      <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={() => setSettingsAnchor(null)} PaperProps={{ sx: { minWidth: 230, p: .5 } }}>
        <Typography sx={{ px: 1.5, py: 1, color: C.ink, fontSize: 12, fontWeight: 850 }}>Row density</Typography>
        {["compact", "standard", "comfortable"].map((option) => <MenuItem key={option} selected={density === option} onClick={() => { setDensity(option); setSettingsAnchor(null); }} sx={{ textTransform: "capitalize" }}>{option}</MenuItem>)}
        <Divider />
        <MenuItem onClick={saveCurrentView}><SaveOutlinedIcon fontSize="small" sx={{ mr: 1 }} />Save current view</MenuItem>
        <MenuItem onClick={() => { resetConfiguration(); setSettingsAnchor(null); }}><RestartAltIcon fontSize="small" sx={{ mr: 1 }} />Reset table</MenuItem>
      </Menu>
    </Box>
  );
}
